import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { centersApi, type CreateCenterInput } from '@/api/centers';

export function useMyCenters(ownerOnly = false) {
  return useQuery({
    queryKey: ['centers', 'my', ownerOnly],
    queryFn: () => centersApi.listMy(ownerOnly),
  });
}

export function useCenter(id: number | null) {
  return useQuery({
    queryKey: ['centers', 'detail', id],
    queryFn: () => centersApi.findById(id!),
    enabled: id != null,
  });
}

export function useApprovedCenters() {
  return useQuery({
    queryKey: ['centers', 'approved'],
    queryFn: () => centersApi.listApproved(),
  });
}

export function useCreateCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCenterInput) => centersApi.create(input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['centers'] }); },
  });
}

export function useUpdateCenter(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<CreateCenterInput>) => centersApi.update(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['centers', 'detail', id] });
      void qc.invalidateQueries({ queryKey: ['centers', 'my'] });
    },
  });
}

export function useDeactivateCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => centersApi.deactivate(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['centers'] }); },
  });
}

export function usePauseCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, until, reason }: { id: number; until: string; reason?: string }) =>
      centersApi.pause(id, until, reason),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['centers'] }); },
  });
}

export function useResumeCenter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => centersApi.resume(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['centers'] }); },
  });
}

export function useIntakeShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, ...opts }: { shipmentId: number; photoUrl?: string; actualWeight?: number; shelfLocation?: string }) =>
      centersApi.intakeShipment(shipmentId, opts),
    onSuccess: (_d, { shipmentId }) => {
      void qc.invalidateQueries({ queryKey: ['shipments', 'detail', shipmentId] });
      void qc.invalidateQueries({ queryKey: ['shipments', 'my'] });
    },
  });
}

export function useHandoffShipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ shipmentId, ...opts }: { shipmentId: number; target: 'driver' | 'receiver'; otpCode?: string }) =>
      centersApi.handoffShipment(shipmentId, opts),
    onSuccess: (_d, { shipmentId }) => {
      void qc.invalidateQueries({ queryKey: ['shipments', 'detail', shipmentId] });
      void qc.invalidateQueries({ queryKey: ['shipments', 'my'] });
    },
  });
}
